import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Sparkles, ArrowRight, Heart, Smile } from "lucide-react";

/**
 * Warm, social, "happy user" login screen.
 *
 * Left panel: an inviting hand-lettered welcome + the sign-in form on
 * a soft cream background.
 *
 * Right panel: a sunset-into-lavender gradient with three lightly-tilted
 * polaroid cards floating in front of a scatter of confetti dots and a
 * film-strip motif — no external image assets are used so the page
 * still loads instantly.
 */
export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [warming, setWarming] = useState(false);   // shown when the server is cold
  const [err, setErr] = useState("");

  if (user && user.id) return <Navigate to="/" replace />;

  // Emergent's production containers spin down when idle and take
  // 30-60s to wake up on the first request. Instead of surfacing a
  // red error toast on each timeout, we silently retry a handful of
  // times and show a friendly "Waking up server…" state after the
  // first slow response.
  const MAX_LOGIN_RETRIES = 6;      // ≈ 6 × 10s = 60s total wake window
  const RETRY_DELAY_MS = 5_000;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    setWarming(false);
    const started = Date.now();
    let attempt = 0;
    while (true) {
      attempt += 1;
      try {
        await login(email.trim(), password);
        nav("/");
        return;
      } catch (ex) {
        // Only retry on transport/gateway failures (cold container);
        // real auth errors (401, 400) must fail fast.
        const status = ex?.response?.status;
        const isTransport = !status || status >= 502;
        const canRetry = isTransport && attempt < MAX_LOGIN_RETRIES;
        if (!canRetry) {
          setErr(
            formatApiErrorDetail(ex?.response?.data?.detail)
              || ex.message
              || "Login failed",
          );
          setLoading(false);
          setWarming(false);
          return;
        }
        // After the first slow / failed attempt, flip to the warming
        // state so the user sees encouraging progress instead of a
        // silent spinner.
        if (Date.now() - started > 4_000 || attempt >= 2) setWarming(true);
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      }
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_1.1fr] bg-[#FFF8F1]">
      {/* ─────────── LEFT · form ─────────── */}
      <div className="relative flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-12 overflow-hidden">
        {/* Soft blurred coral orb behind the form for warmth */}
        <div
          className="absolute -top-20 -left-24 w-[420px] h-[420px] rounded-full opacity-40 blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle at center, #FFB79A, transparent 70%)" }}
          aria-hidden
        />
        <div
          className="absolute -bottom-32 -right-16 w-[380px] h-[380px] rounded-full opacity-30 blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle at center, #B7A6FF, transparent 70%)" }}
          aria-hidden
        />

        <div className="relative max-w-md w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur border border-orange-200 shadow-sm mb-10">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-orange-700">
              PhotoPick&nbsp;Studio
            </span>
          </div>

          <h1
            className="font-heading text-5xl sm:text-6xl font-black tracking-tighter text-slate-900 leading-[0.98]"
            data-testid="login-title"
          >
            Hey there,
            <br />
            <span className="relative inline-block">
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: "linear-gradient(90deg, #F97066 0%, #FF9A62 45%, #B37DFF 100%)",
                }}
              >
                say cheese!
              </span>
              <svg
                className="absolute -bottom-2 left-0 w-full"
                viewBox="0 0 220 12"
                fill="none"
                aria-hidden
              >
                <path
                  d="M2 8 Q 55 2 110 6 T 218 5"
                  stroke="#F97066"
                  strokeWidth="3"
                  strokeLinecap="round"
                  fill="none"
                  opacity="0.55"
                />
              </svg>
            </span>
          </h1>

          <p className="text-slate-600 mt-6 font-medium text-lg leading-relaxed">
            Welcome back to your happiest studio.
            <br />
            <span className="text-slate-500">
              Sign in and let&rsquo;s make some memories &nbsp;
              <Heart className="inline h-4 w-4 text-rose-500 fill-rose-500 -translate-y-0.5" />
            </span>
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label
                htmlFor="email"
                className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500"
              >
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@studio.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/90 backdrop-blur text-slate-900 placeholder:text-slate-400 border border-orange-100 rounded-xl h-12 shadow-sm focus-visible:ring-2 focus-visible:ring-orange-300 focus-visible:border-orange-300"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="password"
                className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500"
              >
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                placeholder="•••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/90 backdrop-blur text-slate-900 placeholder:text-slate-400 border border-orange-100 rounded-xl h-12 shadow-sm focus-visible:ring-2 focus-visible:ring-orange-300 focus-visible:border-orange-300"
                data-testid="login-password"
              />
            </div>

            {err && (
              <p
                className="text-sm text-red-700 bg-red-50 border-l-4 border-destructive px-3 py-2 rounded-r-md"
                data-testid="login-error"
              >
                {err}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full text-white rounded-xl h-12 font-bold text-[15px] shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-[transform,box-shadow] border-0"
              style={{
                background: "linear-gradient(90deg, #F97066 0%, #FF8B57 100%)",
              }}
              data-testid="login-submit"
            >
              {loading ? (
                warming ? (
                  <span className="inline-flex items-center gap-2" data-testid="login-warming">
                    <span
                      className="h-2 w-2 rounded-full bg-white animate-pulse"
                      style={{ boxShadow: "0 0 0 4px rgba(255,255,255,.35)" }}
                    />
                    Waking up server… (this can take up to a minute)
                  </span>
                ) : (
                  "Signing in…"
                )
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Let&rsquo;s go
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-8 text-xs text-slate-400 font-medium flex items-center gap-1.5">
            <Smile className="h-3.5 w-3.5" />
            Made for photographers who love their people.
          </p>
        </div>
      </div>

      {/* ─────────── RIGHT · playful hero ─────────── */}
      <div
        className="hidden lg:block relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #FFD5B0 0%, #FFA07C 26%, #F97066 52%, #C97AE5 78%, #7C6BFF 100%)",
        }}
        aria-hidden
      >
        {/* Confetti dots — hand-picked positions so it looks intentional */}
        <ConfettiDots />

        {/* Film-strip decorative element at the bottom */}
        <div
          className="absolute -bottom-6 -left-8 -rotate-6 h-24 flex items-center gap-2 px-3 rounded-md shadow-2xl"
          style={{ background: "#1E1B3A", width: "70%" }}
        >
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="w-4 h-4 rounded-sm bg-[#FFF8F1]/95" />
          ))}
        </div>

        {/* Three floating polaroids */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-[520px] h-[560px]">
            <Polaroid
              className="absolute top-[6%] left-[4%] rotate-[-11deg] animate-[float_7s_ease-in-out_infinite]"
              tint="linear-gradient(135deg, #FFB37A 0%, #F97066 100%)"
              caption="Emma & Jack · Sept 2025"
              faces={3}
            />
            <Polaroid
              className="absolute top-[24%] right-[4%] rotate-[9deg] animate-[float_9s_ease-in-out_infinite_-2s]"
              tint="linear-gradient(135deg, #B7A6FF 0%, #7C6BFF 100%)"
              caption="Studio Portraits · Oct"
              faces={2}
            />
            <Polaroid
              className="absolute bottom-[6%] left-[18%] rotate-[-4deg] animate-[float_8s_ease-in-out_infinite_-4s]"
              tint="linear-gradient(135deg, #FFE6A2 0%, #FFA07C 100%)"
              caption="The Ridley Family"
              faces={4}
            />
          </div>
        </div>

        {/* Hand-lettered corner headline */}
        <div className="absolute top-14 left-14 max-w-[380px] text-white">
          <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur border border-white/30 text-[11px] uppercase tracking-[0.25em] font-bold">
            <Sparkles className="h-3 w-3" />
            For happy photographers
          </p>
          <p className="font-heading text-4xl xl:text-[46px] font-black leading-[1.02] tracking-tight mt-6 drop-shadow-sm">
            Pick the smiles.
            <br />
            Skip the blinks.
            <br />
            Deliver the joy.
          </p>
        </div>

        {/* Signature quote bottom-right */}
        <div className="absolute bottom-14 right-14 max-w-[280px] text-white/90 text-right">
          <p className="italic font-serif text-lg leading-snug">
            &ldquo;Every shoot deserves the frame that made you smile.&rdquo;
          </p>
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold mt-3 text-white/70">
            — PhotoPick team
          </p>
        </div>

        {/* Keyframes injected once — Tailwind arbitrary keyframes don't
            reliably compile the standalone @keyframes rule so we ship
            it inline. */}
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(var(--tw-rotate)); }
            50%      { transform: translateY(-12px) rotate(var(--tw-rotate)); }
          }
        `}</style>
      </div>
    </div>
  );
}

/**
 * A single tilted polaroid card with a duotone gradient photo, faux
 * face circles, and a hand-written caption. Purely CSS — no external
 * image required so the login page loads instantly even offline.
 */
function Polaroid({ className, tint, caption, faces }) {
  return (
    <div
      className={`w-[220px] rounded-md bg-white p-3 pb-5 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.35)] ${className}`}
    >
      <div
        className="relative h-[220px] rounded-sm overflow-hidden"
        style={{ background: tint }}
      >
        {/* Faux face circles arranged in a group */}
        <div className="absolute inset-0 flex items-end justify-center gap-2 pb-6">
          {Array.from({ length: faces }).map((_, i) => (
            <div key={i} className="flex flex-col items-center">
              <div
                className="w-9 h-9 rounded-full bg-white/95 shadow-inner"
                style={{
                  background:
                    "radial-gradient(circle at 45% 40%, #FFF 0%, #FFD9B7 40%, #E8B091 100%)",
                }}
              />
              <div className="w-11 h-6 -mt-1 rounded-t-lg bg-white/95" />
            </div>
          ))}
        </div>
        {/* Subtle grain / light-leak */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.15) 100%)",
          }}
        />
      </div>
      <p
        className="text-center text-[13px] mt-2 text-slate-700"
        style={{ fontFamily: "'Caveat', 'Segoe Script', cursive" }}
      >
        {caption}
      </p>
    </div>
  );
}

/**
 * Sparse scatter of translucent circles + stars to make the hero panel
 * feel celebratory without turning into a confetti explosion.
 */
function ConfettiDots() {
  const dots = [
    { l: "8%", t: "10%", s: 8, o: 0.9, c: "#FFD583" },
    { l: "22%", t: "80%", s: 12, o: 0.7, c: "#FFF" },
    { l: "40%", t: "6%", s: 6, o: 0.9, c: "#B7A6FF" },
    { l: "48%", t: "92%", s: 10, o: 0.8, c: "#FFF3D6" },
    { l: "70%", t: "12%", s: 14, o: 0.55, c: "#FFF" },
    { l: "82%", t: "76%", s: 8, o: 0.9, c: "#7DE0B4" },
    { l: "92%", t: "22%", s: 10, o: 0.7, c: "#FFF" },
    { l: "12%", t: "48%", s: 6, o: 0.6, c: "#F97066" },
    { l: "58%", t: "58%", s: 5, o: 0.8, c: "#FFD583" },
    { l: "88%", t: "48%", s: 8, o: 0.7, c: "#FFF" },
  ];
  return (
    <>
      {dots.map((d, i) => (
        <span
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: d.l,
            top: d.t,
            width: d.s,
            height: d.s,
            background: d.c,
            opacity: d.o,
            boxShadow: `0 0 ${d.s * 2}px ${d.c}`,
          }}
        />
      ))}
      {/* A few star shapes */}
      {[
        { l: "16%", t: "22%", size: 18 },
        { l: "76%", t: "34%", size: 14 },
        { l: "36%", t: "72%", size: 16 },
      ].map((s, i) => (
        <svg
          key={`star-${i}`}
          viewBox="0 0 24 24"
          className="absolute text-white/80 pointer-events-none"
          style={{
            left: s.l,
            top: s.t,
            width: s.size,
            height: s.size,
            filter: "drop-shadow(0 0 6px rgba(255,255,255,0.55))",
          }}
          fill="currentColor"
        >
          <path d="M12 2 L14 9 L21 9 L15.5 13.5 L17.5 20.5 L12 16.5 L6.5 20.5 L8.5 13.5 L3 9 L10 9 Z" />
        </svg>
      ))}
    </>
  );
}
